import './FieldRangePill.css'
import { MinusIcon, PlusIcon } from '../icons'
import { useRef, useCallback, useEffect } from 'react'
import { NumericFormat } from 'react-number-format'

interface FieldRangePillProps {
  value: number | undefined
  onChange: (value: number | undefined) => void
  controls?: {
    min?: number
    max?: number
    step: number
    /** If set, used for display/input decimals; otherwise derived from step */
    decimalScale?: number
  }
  disabled?: boolean
  className?: string
}

/**
 * Custom hook to handle press-and-hold button behavior
 * @param action - The function to execute on hold
 * @param holdDelay - Delay before continuous execution starts (ms)
 * @param holdInterval - Interval between executions during hold (ms)
 * @returns Handlers for mouse/touch events
 */
function useHoldButton(action: () => void, holdDelay: number = 500, holdInterval: number = 100) {
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const holdIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const clearTimers = useCallback(() => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current)
      holdTimeoutRef.current = null
    }
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current)
      holdIntervalRef.current = null
    }
  }, [])

  const startHold = useCallback(() => {
    // Execute once immediately for instant feedback
    action()

    // Start timeout before continuous execution begins
    holdTimeoutRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        action()
      }, holdInterval)
    }, holdDelay)
  }, [action, holdDelay, holdInterval])

  const stopHold = useCallback(() => {
    clearTimers()
  }, [clearTimers])

  // Cleanup on unmount
  useEffect(() => {
    return () => clearTimers()
  }, [clearTimers])

  return {
    onMouseDown: startHold,
    onMouseUp: stopHold,
    onMouseLeave: stopHold,
    onTouchStart: startHold,
    onTouchEnd: stopHold
  }
}

export default function FieldRangePill({
  value,
  onChange,
  controls = {
    min: undefined,
    max: undefined,
    step: 1
  },
  disabled = false,
  className = ''
}: FieldRangePillProps) {
  const { min, max, step, decimalScale: decimalScaleOverride } = controls

  // Keep refs for latest values to avoid stale closures in interval callbacks
  const valueRef = useRef(value)
  const onChangeRef = useRef(onChange)
  const stepRef = useRef(step)
  const minRef = useRef(min)
  const maxRef = useRef(max)

  // Sync refs with latest prop values
  useEffect(() => {
    valueRef.current = value
    onChangeRef.current = onChange
    stepRef.current = step
    minRef.current = min
    maxRef.current = max
  }, [value, onChange, step, min, max])

  // Core increment/decrement logic
  const handleDecrement = useCallback(() => {
    const currentValue = valueRef.current ?? 0
    const newValue = currentValue - stepRef.current
    if (minRef.current !== undefined && newValue < minRef.current) {return}
    onChangeRef.current(newValue)
  }, [])

  const handleIncrement = useCallback(() => {
    const currentValue = valueRef.current ?? 0
    const newValue = currentValue + stepRef.current
    if (maxRef.current !== undefined && newValue > maxRef.current) {return}
    onChangeRef.current(newValue)
  }, [])

  // Hold behavior for decrement button
  const decrementHoldHandlers = useHoldButton(handleDecrement)

  // Hold behavior for increment button
  const incrementHoldHandlers = useHoldButton(handleIncrement)

  // Disable logic
  const isDecrementDisabled = disabled || (min !== undefined && value !== undefined && value <= min)
  const isIncrementDisabled = disabled || (max !== undefined && value !== undefined && value >= max)

  // Use explicit decimalScale if provided, otherwise derive from step size
  const decimalScale =
    decimalScaleOverride ??
    (() => {
      const stepStr = step.toString()
      const decimalIndex = stepStr.indexOf('.')
      return decimalIndex === -1 ? 0 : stepStr.length - decimalIndex - 1
    })()

  return (
    <div className={`field-range-pill ${disabled ? 'field-range-pill--disabled' : ''} ${className}`}>
      <button
        type="button"
        {...decrementHoldHandlers}
        disabled={isDecrementDisabled}
        className="field-range-pill-button field-range-pill-decrement"
        aria-label="Decrement"
      >
        <MinusIcon className="field-range-pill-icon" />
      </button>

      <NumericFormat
        value={value}
        onValueChange={values => {
          const numValue = values.floatValue
          onChange(numValue)
        }}
        isAllowed={values => {
          const numValue = values.floatValue
          if (numValue === undefined) {return true}

          // Enforce min/max constraints
          if (min !== undefined && numValue < min) {return false}
          if (max !== undefined && numValue > max) {return false}

          return true
        }}
        disabled={disabled}
        allowNegative={min === undefined || min < 0}
        decimalScale={decimalScale}
        className="field-range-pill-input"
        displayType="input"
        thousandSeparator={false}
      />

      <button
        type="button"
        {...incrementHoldHandlers}
        disabled={isIncrementDisabled}
        className="field-range-pill-button field-range-pill-increment"
        aria-label="Increment"
      >
        <PlusIcon className="field-range-pill-icon" />
      </button>
    </div>
  )
}
